/**
 * Form shape, validity, match detection and copy for the Invite drawer.
 */
import { UserMgmtError, type ExistingAccountDetails } from '../../../../api/users';
import { includesDirectorRole } from '../director-regions';
import { ROLE_BY_ID, type UserRoleId } from '../roleTaxonomy';
import type { UserRow } from '../userViewModel';

import type { EmailMatch } from './InviteEmailField';

export interface InviteFormState {
  email: string;
  firstName: string;
  lastName: string;
  roleIds: UserRoleId[];
  regionIds: string[];
}

export function splitName(name: string): { firstName: string; lastName: string } {
  const [first, ...rest] = name.trim().split(/\s+/);
  return { firstName: first ?? '', lastName: rest.join(' ') };
}

/** Matches the typed email against the existing rows: an imported row can be
 *  linked, anything else is a duplicate. */
export function detectMatch(email: string, users: UserRow[]): EmailMatch {
  const q = email.trim().toLowerCase();
  if (!q) return { kind: 'none' };
  const exact = users.find((u) => (u.email ?? '').toLowerCase() === q);
  if (!exact) return { kind: 'none' };
  if (exact.status === 'imported') return { kind: 'backfill', user: exact };
  return { kind: 'duplicate', user: exact };
}

/** Whether any selected role is region-scoped, which is what shows the region
 *  picker. */
export function needsRegions(roleIds: UserRoleId[]): boolean {
  return roleIds.some((id) => ROLE_BY_ID[id]?.scope === 'region');
}

function hasRequiredFields(form: InviteFormState): boolean {
  return (
    form.email.includes('@') &&
    form.firstName.trim().length > 0 &&
    form.lastName.trim().length > 0 &&
    form.roleIds.length > 0
  );
}

function regionsFitRoles(form: InviteFormState, requiresRegion: boolean): boolean {
  if (includesDirectorRole(form.roleIds)) return form.regionIds.length === 1;
  return !requiresRegion || form.regionIds.length > 0;
}

export function isFormValid(
  form: InviteFormState,
  match: EmailMatch,
  requiresRegion: boolean,
): boolean {
  return (
    hasRequiredFields(form) && match.kind !== 'duplicate' && regionsFitRoles(form, requiresRegion)
  );
}

export const MAX_NAME_MATCHES = 5;

function nameTokens(value: string): string[] {
  return value
    .toLowerCase()
    .split(/[\s,.'-]+/)
    .filter((t) => t.length >= 2);
}

/**
 * Imported rows whose name shares a token with the typed one, best match first.
 * Rows carrying a different email are left out, since they cannot be linked.
 */
export function findImportedNameMatches(
  form: Pick<InviteFormState, 'firstName' | 'lastName' | 'email'>,
  users: UserRow[],
): UserRow[] {
  const typed = nameTokens(`${form.firstName} ${form.lastName}`);
  if (typed.length === 0) return [];
  const email = form.email.trim().toLowerCase();
  return users
    .filter((u) => u.status === 'imported')
    .filter((u) => !u.email || u.email.toLowerCase() === email)
    .map((u) => ({
      user: u,
      score: nameTokens(u.name).filter((t) => typed.includes(t)).length,
    }))
    .filter((m) => m.score > 0)
    .sort((a, b) => b.score - a.score || a.user.name.localeCompare(b.user.name))
    .slice(0, MAX_NAME_MATCHES)
    .map((m) => m.user);
}

const ALREADY_INVITED =
  'That person has already been invited or has signed in. Refresh the list — you may only need to resend their invite.';

const INVITE_ERROR_TEXT: Record<string, string> = {
  LINK_ALREADY_LINKED: ALREADY_INVITED,
  LINK_NOT_IMPORTED: ALREADY_INVITED,
  LINK_EMAIL_MISMATCH:
    "The linked staff row's email no longer matches the email you typed. Refresh the list and try again.",
  LINK_NOT_FOUND:
    'The staff row we tried to link no longer exists. It may have been removed. Refresh the list.',
  RD_FORBIDDEN_ROLE:
    'Regional Directors can only invite Instructors. Pick the Instructor role only, or ask an Admin to handle this invite.',
  RD_OUT_OF_SCOPE_REGION:
    'One or more selected regions are outside your scope. Pick regions you manage, or ask an Admin.',
  RD_NEEDS_ONE_REGION: 'A Regional Director has exactly one region. Pick a single region.',
  EMAIL_IN_USE:
    'Another user in Nebari already has this email. If they are the same person, their two records need merging — inviting again would lock them out.',
  AUTH0_ACCOUNT_IN_USE:
    'That login is already attached to another Nebari user. Find them in the list instead of inviting again.',
  AUTH0_ACCOUNT_BLOCKED:
    'This email has a BonsaiBar login that has been deactivated. Reactivate the user first, then retry.',
  AUTH0_ACCOUNT_ROLES_FORBIDDEN:
    'This email already has a BonsaiBar login with roles only an Admin can grant. Ask an Admin to add them.',
  REGION_HAS_DIRECTOR:
    'That region already has a director. Clear them on the region page first, then retry.',
  TARGET_OUTRANKS_CALLER: 'Only an Admin can change this user.',
};

/** Operator-friendly drawer text for a backend error code, falling back to the
 *  server's own message. */
export function explainInviteError(err: unknown): string {
  if (!(err instanceof UserMgmtError)) {
    return err instanceof Error ? err.message : 'Something went wrong. Please retry.';
  }
  return (err.code && INVITE_ERROR_TEXT[err.code]) || err.message;
}

/** The header subtitle describing what this invite will do. */
export function inviteSubtitle(isLink: boolean): string {
  const how = 'email them to sign in with Google, or set a password if they prefer';
  return isLink
    ? `We'll link this sign-in to the existing FareHarbor staff record — no duplicate row — and ${how}.`
    : `We'll ${how}. They appear as Invited until their first sign-in.`;
}

function roleList(roles: string[]): string {
  if (roles.length === 0) return 'It has no roles yet';
  const names = roles.map((r) => ROLE_BY_ID[r as UserRoleId]?.name ?? r);
  return `It already has: ${names.join(', ')}. Those stay, and the roles picked here are added`;
}

/** The confirm notice shown before an existing login is linked. */
export function linkExistingAccountText(details: ExistingAccountDetails | undefined): string {
  const intro = 'This email already has a BonsaiBar login (usually from staging).';
  const outro = 'No invite email will be sent, so let them know they can sign in.';
  if (!details) return `${intro} Link it to add them here with the same login. ${outro}`;
  const how = details.signInMethod === 'google-oauth2' ? 'with Google' : 'with their password';
  return `${intro} ${roleList(details.existingRoles)}. They'll keep signing in ${how}, as they do now. ${outro}`;
}

export function inviteSuccessText(emailSent: boolean, linkedExistingAccount: boolean): string {
  if (linkedExistingAccount) {
    return 'Account linked. They can sign in with their existing login. No email was sent.';
  }
  if (!emailSent) {
    return 'User created, but the invite email failed to send. Use "Resend invite" on their row.';
  }
  return 'Invite sent. They can sign in with Google using this address, or set a password.';
}
